// Shared Header Component for AAR Guest Management System
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { logout, getCurrentUser, getUserRole, endViewAs } from '../firebase/authService';
import { getViewAs } from '../utils/viewAs';
import { canAccessAdmin, canCheckInOut, canAccessGuestProfiles, canAccessReports, getRoleName } from '../utils/roles';
import { subscribeToCheckInsWithNotes, subscribeToUnreadVolunteerNotes } from '../firebase/firestoreService';
import APP_VERSION from '../version';
import { useAppUpdate } from '../utils/appUpdate';
import '../styles/Header.css';

const Header = () => {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [currentDateTime, setCurrentDateTime] = useState(new Date());
  const [showReportsDropdown, setShowReportsDropdown] = useState(false);
  const [showProfilesDropdown, setShowProfilesDropdown] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [unreadNotesCount, setUnreadNotesCount] = useState(0);
  const [volunteerNotesCount, setVolunteerNotesCount] = useState(0);
  const [isTablet, setIsTablet] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth >= 769 && window.innerWidth <= 1024;
  });
  const navigate = useNavigate();
  const location = useLocation();

  const { updateAvailable, latestVersion, reload } = useAppUpdate();
  const viewAs = getViewAs();
  const [exitingViewAs, setExitingViewAs] = useState(false);

  useEffect(() => {
    const currentUser = getCurrentUser();
    if (currentUser) {
      setUser(currentUser);
      getUserRole(currentUser.uid).then(role => {
        setUserRole(role);
      });
    }
  }, []);

  // Update date/time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDateTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Track tablet viewport (769–1024px) to render abbreviated header text
  useEffect(() => {
    const onResize = () => {
      setIsTablet(window.innerWidth >= 769 && window.innerWidth <= 1024);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Subscribe to unread notes count
  useEffect(() => {
    if (!user) return;

    const unsubscribe = subscribeToCheckInsWithNotes(user.uid, (checkInsWithNotes) => {
      const unreadCount = checkInsWithNotes.filter(checkIn => !checkIn.isRead).length;
      setUnreadNotesCount(unreadCount);
    });

    return () => unsubscribe();
  }, [user]);

  // Subscribe to unread volunteer check-in notes (same per-user read tracking
  // as guest notes, so the count clears once the notes have been viewed)
  useEffect(() => {
    if (!user) return;

    const unsubscribe = subscribeToUnreadVolunteerNotes(user.uid, (counts) => {
      setVolunteerNotesCount(Object.values(counts).reduce((sum, n) => sum + n, 0));
    });

    return () => unsubscribe();
  }, [user]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showReportsDropdown && !event.target.closest('.reports-dropdown')) {
        setShowReportsDropdown(false);
      }
      if (showProfilesDropdown && !event.target.closest('.profiles-dropdown')) {
        setShowProfilesDropdown(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showReportsDropdown, showProfilesDropdown]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleNavigation = (e, path) => {
    e.preventDefault();
    navigate(path);
  };

  const isActive = (path) => location.pathname === path;

  const isReportsActive = () => {
    return location.pathname === '/reports' ||
           location.pathname === '/timeline-report' ||
           location.pathname === '/admin/pager-report' ||
           location.pathname === '/admin/guest-pickup-report' ||
           location.pathname === '/incident-reports';
  };

  const isCheckInActive = () => {
    return location.pathname === '/check-in' || location.pathname === '/volunteer-check-in';
  };

  const isProfilesActive = () => {
    return location.pathname === '/guest-profiles' || location.pathname === '/volunteer-profiles';
  };

  // Get greeting based on time of day
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) {
      return { text: isTablet ? 'GM' : 'Good Morning', icon: '🌅' };
    } else if (hour >= 12 && hour < 17) {
      return { text: isTablet ? 'GA' : 'Good Afternoon', icon: '☀️' };
    } else if (hour >= 17 && hour < 21) {
      return { text: isTablet ? 'GE' : 'Good Evening', icon: '🌆' };
    } else {
      return { text: isTablet ? 'GN' : 'Good Night', icon: '🌙' };
    }
  };

  // Get display name from user
  const getDisplayName = () => {
    if (user?.displayName) {
      return user.displayName;
    } else if (user?.email) {
      // Extract name from email (before @)
      return user.email.split('@')[0];
    }
    return 'Guest';
  };

  // Format date and time
  const formatDateTime = () => {
    const options = isTablet
      ? { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' }
      : { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    let dateStr = currentDateTime.toLocaleDateString('en-US', options);
    if (isTablet) {
      // Uppercase the weekday (e.g. "Thu, Apr 30, 2026" → "THU, Apr 30, 2026")
      dateStr = dateStr.replace(/^([A-Za-z]{3})/, (m) => m.toUpperCase());
    }
    const timeStr = currentDateTime.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
    return { date: dateStr, time: timeStr };
  };

  const greeting = getGreeting();
  const dateTime = formatDateTime();

  return (
    <>
    {/* Nothing in normal use reloads the page — routing is client-side and
        sign-in uses a popup — so a tab can run an old bundle indefinitely.
        useAppUpdate reloads the tab itself once it is safe; this banner is
        what the operator sees in the meantime (e.g. while a check-in is in
        progress), with a button to take the update right away. */}
    {viewAs && (
      <div className="view-as-banner" role="status">
        <span className="view-as-text">
          Viewing as <strong>{viewAs.displayName || viewAs.email}</strong>
          {viewAs.role ? ` (${getRoleName(viewAs.role)})` : ''} — read-only. Nothing can be changed until you exit.
        </span>
        <button
          type="button"
          className="view-as-exit-btn"
          disabled={exitingViewAs}
          onClick={() => { setExitingViewAs(true); endViewAs(); }}
        >
          {exitingViewAs ? 'Exiting…' : 'Exit and return to my role'}
        </button>
      </div>
    )}
    {updateAvailable && (
      <div className="app-update-banner" role="status">
        <span className="app-update-text">
          A newer version of the app
          {latestVersion ? ` (v${latestVersion})` : ''} is available — this page will refresh automatically once you finish what you're doing.
        </span>
        <button type="button" className="app-update-btn" onClick={reload}>
          Reload now
        </button>
      </div>
    )}
    <header className="app-header">
      {/* Desktop Header Layout */}
      <div className="header-left">
        <div className="header-logo-title" onClick={(e) => handleNavigation(e, '/dashboard')}>
          <img
            src="/bt_logo.jpg"
            alt="Brooklyn Tabernacle Logo"
            className="header-logo"
          />
          <h1>{isTablet ? 'AGMS' : 'Access-A-Ride Guest Management'}</h1>
          <span className="header-version">v{APP_VERSION}</span>
        </div>
      </div>
      <div className="header-user-info">
        <div className="greeting-section">
          <span className="greeting-icon">{greeting.icon}</span>
          <span className="greeting-message">{greeting.text}, {getDisplayName()}</span>
          <span className="separator-pipe">|</span>
          <span className="datetime-text">{dateTime.date}</span>
          <span className="separator-pipe">|</span>
          <span className="datetime-text">{dateTime.time}</span>
        </div>
      </div>

      {/* Mobile Header Layout */}
      <div className="mobile-header-container">
        <div className="mobile-header-left">
          <img
            src="/bt_logo.jpg"
            alt="Brooklyn Tabernacle Logo"
            className="header-logo-mobile"
            onClick={(e) => handleNavigation(e, '/dashboard')}
          />
        </div>
        <div className="mobile-header-center">
          <h1 className="mobile-header-title" onClick={(e) => handleNavigation(e, '/dashboard')}>BT AGMS</h1>
          <span className="header-version-mobile">v{APP_VERSION}</span>
        </div>
        <div className="mobile-header-right">
          <button
            className="hamburger-menu"
            onClick={() => setShowMobileMenu(!showMobileMenu)}
            aria-label="Menu"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {showMobileMenu && (
        <div className="mobile-menu-overlay" onClick={() => setShowMobileMenu(false)}>
          <div className="mobile-menu" onClick={(e) => e.stopPropagation()}>
            <span
              onClick={(e) => {
                handleNavigation(e, '/dashboard');
                setShowMobileMenu(false);
              }}
              className={`mobile-menu-item ${isActive('/dashboard') ? 'active' : ''}`}
            >
              Dashboard
            </span>
            {canCheckInOut(userRole) && (
              <>
                <span
                  onClick={(e) => {
                    handleNavigation(e, '/check-in');
                    setShowMobileMenu(false);
                  }}
                  className={`mobile-menu-item ${isActive('/check-in') ? 'active' : ''}`}
                >
                  ✅ Check In Guest
                </span>
                <span
                  onClick={(e) => {
                    handleNavigation(e, '/volunteer-check-in');
                    setShowMobileMenu(false);
                  }}
                  className={`mobile-menu-item ${isActive('/volunteer-check-in') ? 'active' : ''}`}
                >
                  🙋 Volunteer Check-In
                </span>
              </>
            )}
            <span
              onClick={(e) => {
                handleNavigation(e, '/guest-profiles?unread=true');
                setShowMobileMenu(false);
              }}
              className="mobile-menu-item"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
            >
              <span>🔔 Guest Notes</span>
              {unreadNotesCount > 0 && (
                <span className="mobile-notification-badge mobile-guest-badge">{unreadNotesCount}</span>
              )}
            </span>
            <span
              onClick={(e) => {
                handleNavigation(e, '/volunteer-profiles?unread=true');
                setShowMobileMenu(false);
              }}
              className="mobile-menu-item"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
            >
              <span>🔔 Volunteer Notes</span>
              {volunteerNotesCount > 0 && (
                <span className="mobile-notification-badge mobile-volunteer-badge">{volunteerNotesCount}</span>
              )}
            </span>
            {canAccessGuestProfiles(userRole) && (
              <>
                <span
                  onClick={(e) => {
                    handleNavigation(e, '/guest-profiles');
                    setShowMobileMenu(false);
                  }}
                  className={`mobile-menu-item ${isActive('/guest-profiles') ? 'active' : ''}`}
                >
                  📋 Guest Profiles
                </span>
                <span
                  onClick={(e) => {
                    handleNavigation(e, '/volunteer-profiles');
                    setShowMobileMenu(false);
                  }}
                  className={`mobile-menu-item ${isActive('/volunteer-profiles') ? 'active' : ''}`}
                >
                  📋 Volunteer Profiles
                </span>
              </>
            )}
{canAccessReports(userRole) && (
              <>
                <span
                  onClick={() => setShowReportsDropdown(!showReportsDropdown)}
                  className={`mobile-menu-item ${isReportsActive() ? 'active' : ''}`}
                >
                  Reports {showReportsDropdown ? '▲' : '▼'}
                </span>
                {showReportsDropdown && (
                  <div className="mobile-submenu">
                    <div
                      className="mobile-menu-item submenu-item"
                      onClick={(e) => {
                        handleNavigation(e, '/reports');
                        setShowMobileMenu(false);
                        setShowReportsDropdown(false);
                      }}
                    >
                      📊 Guest Activities
                    </div>
                    <div
                      className="mobile-menu-item submenu-item"
                      onClick={(e) => {
                        handleNavigation(e, '/timeline-report');
                        setShowMobileMenu(false);
                        setShowReportsDropdown(false);
                      }}
                    >
                      📈 Timeline Reports
                    </div>
                    <div
                      className="mobile-menu-item submenu-item"
                      onClick={(e) => {
                        handleNavigation(e, '/admin/pager-report');
                        setShowMobileMenu(false);
                        setShowReportsDropdown(false);
                      }}
                    >
                      📟 Pager Report
                    </div>
                    <div
                      className="mobile-menu-item submenu-item"
                      onClick={(e) => {
                        handleNavigation(e, '/admin/guest-pickup-report');
                        setShowMobileMenu(false);
                        setShowReportsDropdown(false);
                      }}
                    >
                      🚗 Guest Pickup Report
                    </div>
                    <div
                      className="mobile-menu-item submenu-item"
                      onClick={(e) => {
                        handleNavigation(e, '/incident-reports');
                        setShowMobileMenu(false);
                        setShowReportsDropdown(false);
                      }}
                    >
                      📋 Incident Reports
                    </div>
                  </div>
                )}
              </>
            )}
            {canAccessAdmin(userRole) && (
              <span
                onClick={(e) => {
                  handleNavigation(e, '/admin');
                  setShowMobileMenu(false);
                }}
                className={`mobile-menu-item ${isActive('/admin') ? 'active' : ''}`}
              >
                Admin
              </span>
            )}
            <span
              onClick={() => {
                handleLogout();
                setShowMobileMenu(false);
              }}
              className="mobile-menu-item"
            >
              Logout
            </span>
          </div>
        </div>
      )}

      {/* Desktop Navigation */}
      <div className="header-right">
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'nowrap', whiteSpace: 'nowrap' }}>
          {canCheckInOut(userRole) && (
            <>
              <button
                className="btn btn-secondary"
                onClick={(e) => handleNavigation(e, '/check-in')}
                style={{ padding: '8px 16px', fontSize: '14px', opacity: isActive('/check-in') ? 0.6 : 1 }}
              >
                Check In Guest
              </button>
              <div
                className="notification-bell-container guest-bell"
                onClick={(e) => handleNavigation(e, '/guest-profiles?unread=true')}
                title={unreadNotesCount > 0 ? `${unreadNotesCount} guest note${unreadNotesCount > 1 ? 's' : ''}` : 'No guest notes'}
              >
                <span className="notification-bell">🔔</span>
                {unreadNotesCount > 0 && (
                  <span className="notification-badge guest-badge">{unreadNotesCount}</span>
                )}
              </div>
              <button
                className="btn btn-secondary"
                onClick={(e) => handleNavigation(e, '/volunteer-check-in')}
                style={{ padding: '8px 16px', fontSize: '14px', background: 'linear-gradient(135deg, #667eea 0%, #5568d3 100%)', opacity: isActive('/volunteer-check-in') ? 0.6 : 1 }}
              >
                Volunteer Check-In
              </button>
              <div
                className="notification-bell-container volunteer-bell"
                onClick={(e) => handleNavigation(e, '/volunteer-profiles?unread=true')}
                title={volunteerNotesCount > 0 ? `${volunteerNotesCount} unread volunteer note${volunteerNotesCount > 1 ? 's' : ''}` : 'No unread volunteer notes'}
              >
                <span className="notification-bell">🔔</span>
                {volunteerNotesCount > 0 && (
                  <span className="notification-badge volunteer-badge">{volunteerNotesCount}</span>
                )}
              </div>
              <span style={{ color: 'rgba(255,255,255,0.3)' }}>|</span>
            </>
          )}
          <span
            className={`nav-link ${isActive('/dashboard') ? 'active' : ''}`}
            onClick={(e) => handleNavigation(e, '/dashboard')}
          >
            Dashboard
          </span>
          {canAccessGuestProfiles(userRole) && (
            <>
              <span style={{ color: 'rgba(255,255,255,0.3)' }}>|</span>
              <div className="profiles-dropdown">
                <span
                  className={`nav-link ${isProfilesActive() ? 'active' : ''}`}
                  onClick={() => setShowProfilesDropdown(!showProfilesDropdown)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  Profiles
                  <span style={{ fontSize: '10px' }}>{showProfilesDropdown ? '\u25B2' : '\u25BC'}</span>
                </span>
                {showProfilesDropdown && (
                  <div className="dropdown-menu">
                    <div
                      className="dropdown-item"
                      onClick={(e) => {
                        handleNavigation(e, '/guest-profiles');
                        setShowProfilesDropdown(false);
                      }}
                    >
                      Guest Profiles
                    </div>
                    <div
                      className="dropdown-item"
                      onClick={(e) => {
                        handleNavigation(e, '/volunteer-profiles');
                        setShowProfilesDropdown(false);
                      }}
                    >
                      Volunteer Profiles
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
{canAccessReports(userRole) && (
            <>
            <span style={{ color: 'rgba(255,255,255,0.3)' }}>|</span>
            <div className="reports-dropdown">
              <span
                className={`nav-link ${isReportsActive() ? 'active' : ''}`}
                onClick={() => setShowReportsDropdown(!showReportsDropdown)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                Reports
                <span style={{ fontSize: '10px' }}>{showReportsDropdown ? '▲' : '▼'}</span>
              </span>
              {showReportsDropdown && (
                <div className="dropdown-menu">
                  <div
                    className="dropdown-item"
                    onClick={(e) => {
                      handleNavigation(e, '/reports');
                      setShowReportsDropdown(false);
                    }}
                  >
                    📊 Guest Activities
                  </div>
                  <div
                    className="dropdown-item"
                    onClick={(e) => {
                      handleNavigation(e, '/timeline-report');
                      setShowReportsDropdown(false);
                    }}
                  >
                    📈 Timeline Reports
                  </div>
                  <div
                    className="dropdown-item"
                    onClick={(e) => {
                      handleNavigation(e, '/admin/pager-report');
                      setShowReportsDropdown(false);
                    }}
                  >
                    📟 Pager Report
                  </div>
                  <div
                    className="dropdown-item"
                    onClick={(e) => {
                      handleNavigation(e, '/admin/guest-pickup-report');
                      setShowReportsDropdown(false);
                    }}
                  >
                    🚗 Guest Pickup Report
                  </div>
                  <div
                    className="dropdown-item"
                    onClick={(e) => {
                      handleNavigation(e, '/incident-reports');
                      setShowReportsDropdown(false);
                    }}
                  >
                    📋 Incident Reports
                  </div>
                </div>
              )}
            </div>
            </>
          )}
          {canAccessAdmin(userRole) && (
            <>
              <span style={{ color: 'rgba(255,255,255,0.3)' }}>|</span>
              <span
                className={`nav-link ${isActive('/admin') ? 'active' : ''}`}
                onClick={(e) => handleNavigation(e, '/admin')}
              >
                Admin
              </span>
            </>
          )}
          <span style={{ color: 'rgba(255,255,255,0.3)' }}>|</span>
          <span
            onClick={handleLogout}
            style={{ cursor: 'pointer', fontSize: '14px', color: 'white', fontWeight: '500' }}
            onMouseEnter={(e) => e.target.style.color = '#e0e0e0'}
            onMouseLeave={(e) => e.target.style.color = 'white'}
          >
            Logout
          </span>
        </div>
      </div>
    </header>
    </>
  );
};

export default Header;
